import * as THREE from 'three';
import { meshGrid, type MeshData } from '@vm/shared';

const NORMALS = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];
const AO = [0.5, 0.68, 0.85, 1];
const tmpColor = new THREE.Color();

export interface ModelGeometry {
  lit: THREE.BufferGeometry | null;
  glow: THREE.BufferGeometry | null;
}

/**
 * Converts a small voxel grid into lit + emissive geometries with baked vertex colors and AO.
 * palette: index -> 0xRRGGBB; glowFlags: index -> nonzero if emissive.
 * Positions are ((voxel - offset) * scale).
 */
export function buildModelGeometry(
  grid: Uint8Array,
  sx: number,
  sy: number,
  sz: number,
  palette: ArrayLike<number>,
  glowFlags: Uint8Array,
  scale: number,
  offset: [number, number, number] = [0, 0, 0],
): ModelGeometry {
  // meshGrid only consults the transparent bit; we split emissive faces ourselves.
  const mesh = meshGrid(grid, sx, sy, sz, NO_FLAGS);
  const data = mesh.opaque;
  if (!data) return { lit: null, glow: null };
  return split(data, palette, glowFlags, scale, offset);
}

const NO_FLAGS = new Uint8Array(256);

function split(data: MeshData, palette: ArrayLike<number>, glowFlags: Uint8Array, scale: number, offset: [number, number, number]): ModelGeometry {
  const quads = data.vertexCount / 4;
  let glowQuads = 0;
  for (let q = 0; q < quads; q++) if (glowFlags[data.mat[q * 16]]) glowQuads++;
  const build = (glow: boolean): THREE.BufferGeometry | null => {
    const n = glow ? glowQuads : quads - glowQuads;
    if (n === 0) return null;
    const pos = new Float32Array(n * 4 * 3);
    const nor = new Float32Array(n * 4 * 3);
    const col = new Float32Array(n * 4 * 3);
    const idx = new Uint32Array(n * 6);
    let o = 0;
    for (let q = 0; q < quads; q++) {
      const pal = data.mat[q * 16];
      if (!!glowFlags[pal] !== glow) continue;
      tmpColor.setHex(palette[pal] ?? 0xff00ff);
      const base = o * 4;
      for (let k = 0; k < 4; k++) {
        const v = q * 4 + k;
        const pi = v * 4;
        const i3 = (base + k) * 3;
        pos[i3] = (data.pos[pi] - offset[0]) * scale;
        pos[i3 + 1] = (data.pos[pi + 1] - offset[1]) * scale;
        pos[i3 + 2] = (data.pos[pi + 2] - offset[2]) * scale;
        const nn = NORMALS[data.pos[pi + 3]];
        nor[i3] = nn[0];
        nor[i3 + 1] = nn[1];
        nor[i3 + 2] = nn[2];
        const ao = glow ? 1 : AO[data.mat[pi + 1]];
        col[i3] = tmpColor.r * ao;
        col[i3 + 1] = tmpColor.g * ao;
        col[i3 + 2] = tmpColor.b * ao;
      }
      // Copy the quad's triangulation (indices are relative to the source quad).
      for (let t = 0; t < 6; t++) {
        const srcIndex = data.index[q * 6 + t];
        idx[o * 6 + t] = base + (srcIndex - q * 4);
      }
      o++;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    g.computeBoundingSphere();
    return g;
  };
  return { lit: build(false), glow: build(true) };
}

/** Shared materials for voxel models. */
export const modelMaterials = {
  lit: new THREE.MeshLambertMaterial({ vertexColors: true }),
  glow: new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false }),
  litNoFog: new THREE.MeshLambertMaterial({ vertexColors: true, fog: false }),
  glowNoFog: new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false, fog: false }),
};
